import type { Client } from '../types/crm';
import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';

function norm(value?: string | null): string {
  return (value ?? '').trim();
}

function changeLine(label: string, before: string, after: string): string | null {
  if (before === after) return null;
  const from = before || '—';
  const to = after || '—';
  return `${label}: ${from} → ${to}`;
}

export interface ProfileFormValues {
  company_name: string;
  client_type: string;
  main_phone: string;
  website: string;
  uen: string;
  description: string;
  address: string;
  district: string;
}

export function buildProfileChangeDetail(
  before: Client,
  after: ProfileFormValues,
): string | null {
  const lines = [
    changeLine('Company name', norm(before.company_name), norm(after.company_name)),
    changeLine('Account type', norm(before.client_type), norm(after.client_type)),
    changeLine('Phone', norm(before.main_phone), norm(after.main_phone)),
    changeLine('Website', norm(before.website), norm(after.website)),
    changeLine('UEN', norm(before.uen), norm(after.uen)),
    changeLine('Description', norm(before.description), norm(after.description)),
    changeLine('Address', norm(before.address), norm(after.address)),
    changeLine('District', norm(before.district), norm(after.district)),
  ].filter((line): line is string => Boolean(line));

  if (lines.length === 0) return null;
  return lines.join('\n');
}

export async function logAccountActivityEvent(
  clientId: string,
  eventType: string,
  title: string,
  detail: string,
): Promise<void> {
  const { error } = await getDbSupabase().rpc('log_account_activity_event', {
    p_client_id: Number(clientId),
    p_event_type: eventType,
    p_title: title,
    p_detail: detail,
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function logAccountProfileUpdate(
  clientId: string,
  before: Client,
  after: ProfileFormValues,
): Promise<void> {
  const detail = buildProfileChangeDetail(before, after);
  if (!detail) return;
  await logAccountActivityEvent(clientId, 'profile_updated', 'Profile updated', detail);
}

export async function logAccountCreated(clientId: string, companyName: string): Promise<void> {
  await logAccountActivityEvent(
    clientId,
    'account_created',
    'Account created',
    `Account "${companyName}" was created.`,
  );
}

export async function logAccountCodeAssigned(
  clientId: string,
  fromCode: string,
  toCode: string,
): Promise<void> {
  await logAccountActivityEvent(
    clientId,
    'code_assigned',
    'Customer code assigned',
    `Temporary code ${fromCode} → ${toCode}`,
  );
}
